/**
 * TemperatureTagFilterBody Component
 *
 * Renders serving temperature text pills (warm, chilled)
 * inside the FiltersModal body for the Tags tab.
 * Multi-select: both temperatures can be active at the same time.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { PILL_UNSELECTED, PILL_SELECTED } from './filterChipStyles';

/* ---- Layout constants ---- */
const WRAP_CLASSES = 'flex flex-wrap gap-4';

/* ---- Pill classes ---- */
const PILL_BASE = 'border rounded-xs px-4 py-2 cursor-pointer select-none outline-none transition-[color,background-color,border-color,transform,opacity] ease-in-out active:scale-95 focus-visible:border-brand-yellow font-inter text-sm font-normal leading-normal text-center whitespace-nowrap';

/* ---- Types ---- */
interface TemperatureTagFilterBodyProps {
  selectedTags: string[];
  onToggle: (temperature: string) => void;
}

interface TemperatureOption {
  id: string;
  label: string;
}

/* ---- Data ---- */
// Mirrors Product::ServingTemperatures::ALL (app/models/product.rb)
const TEMPERATURE_OPTIONS: TemperatureOption[] = [
  { id: 'warm', label: 'warm' },
  { id: 'chilled', label: 'chilled' },
];

/* ---- Component ---- */
export function TemperatureTagFilterBody({ selectedTags, onToggle }: TemperatureTagFilterBodyProps) {
  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-inter text-sm font-semibold leading-normal text-grey">
        Serving temp
      </span>
      <div className={WRAP_CLASSES}>
        {TEMPERATURE_OPTIONS.map(({ id, label }) => {
          const isSelected = selectedSet.has(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              className={cn(PILL_BASE, isSelected ? PILL_SELECTED : PILL_UNSELECTED)}
              onClick={() => onToggle(id)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
